import {
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';
import { AppStateFacade } from '@app-state';
import { UiModalComponent } from '@components';
import {
    AssetType,
    defaultValueFor,
} from '@components/ui-value-type/ui-value-type.component';
import {
    AssetScope,
    CredentialStoreResourceType,
    FolderType,
    IAsset,
    IAssetType,
    ICredentialStore,
    ModalResultType,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import {
    AssetService,
    CredentialStoresService,
} from '@services';
import {
    ISuggestValue,
    ISuggestValueData,
} from '@uipath/angular/components/ui-suggest';
import UiValidators, {
    UI_ASSET_DESCRIPTION_MAX_LENGTH,
    UI_ASSET_NAME_MAX_LENGTH,
} from '@validators';

import {
    BehaviorSubject,
    forkJoin,
    Subject,
} from 'rxjs';
import {
    finalize,
    map,
    takeUntil,
} from 'rxjs/operators';

import {
    UiRobotValueEditorComponent,
    UiSingleValueEditorComponent,
} from '../../components';
import { generatePayloadFrom } from '../../utils';

@Component({
    templateUrl: './asset.modal.html',
    styleUrls: ['./asset.modal.scss'],
    providers: [FormBuilder, AssetService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class AssetModalComponent
    extends UiModalComponent<IAsset>
    implements OnInit, OnDestroy {

    public get selectedType() {
        return this.valueTypes.filter(valueType => valueType.id === this.context.data.type);
    }

    public get hasRobots() {
        return this.robotValueEditor && this.robotValueEditor.robotAssets.values.length > 0;
    }

    @HostBinding('class.ui-asset-modal')
    public className = true;

    @ViewChild(UiSingleValueEditorComponent, { static: false })
    public singleValueEditor!: UiSingleValueEditorComponent;

    @ViewChild(UiRobotValueEditorComponent, { static: false })
    public robotValueEditor!: UiRobotValueEditorComponent;

    public credentialStoreChange = new Subject();
    public isReadOnly$ = new BehaviorSubject<boolean | undefined>(undefined);
    public loadingData$ = new BehaviorSubject<boolean>(true);

    public isValidPerRobot = false;
    public valueTypes: ISuggestValue[] = [];
    public credentialStoreDropdownEntries?: ISuggestValueData<ICredentialStore>[];
    public isModernFolder = false;

    public isDefaultChecked!: boolean;

    public loading$ = new BehaviorSubject(false);
    public formGroup!: FormGroup;

    private _destroyed$ = new Subject();

    private _assetService = this._injector.get<AssetService>(AssetService);
    private _credentialService = this._injector.get<CredentialStoresService>(CredentialStoresService);
    private _translateService = this._injector.get<TranslateService>(TranslateService);
    private _appState = this._injector.get<AppStateFacade>(AppStateFacade);

    public hasGlobalValue() {
        const data = this.context.data;
        return data.scope === AssetScope[AssetScope.Global] || data.value != null;
    }

    ngOnInit() {
        this.isModernFolder = this._appState.user.folder.type === FolderType.Modern;

        if (this.context.isNewEntry) {
            this.context.data.type = AssetType.Text as IAssetType;
            this.isDefaultChecked = true;
        } else {
            this.isDefaultChecked = this.hasGlobalValue();
        }

        this.context.data.robotValues = this.context.data.robotValues || [];
        this.valueTypes = Object.keys(AssetType)
            .filter((key) => isNaN(Number.parseInt(key, 10)))
            .map(valueType => ({
                id: valueType,
                text: this._translateService.instant(`AssetType_${valueType}`),
            }));

        const robotCredentialStore =
            this.context.data.robotValues &&
            this.context.data.robotValues.length &&
            this.context.data.robotValues[0].credentialStoreId;
        const storedCredentialStore = this.context.data.credentialStoreId || robotCredentialStore;

        const credentialStores$ = this._credentialService.get().pipe(
            map(stores => stores.data.filter(store => store.details)),
            map((stores) => this.credentialStoreDropdownEntries = stores.map(store => ({
                id: store.id,
                text: store.name,
                data: store,
            }))),
        );

        const defaultAssetCredentialStore$ = this._credentialService.getDefaultStore(CredentialStoreResourceType.AssetCredential);

        forkJoin([
            credentialStores$,
            defaultAssetCredentialStore$,
        ]).pipe(
            finalize(() => this.loadingData$.next(false)),
        ).subscribe(([credentialStores, defaultCredentialStoreId]) => {
            let selectedCredentialStore = credentialStores.filter(cs => cs.id === (storedCredentialStore || defaultCredentialStoreId));
            if (!selectedCredentialStore.length) {
                selectedCredentialStore = credentialStores.filter(cs => cs.id === defaultCredentialStoreId);
            }
            this.formGroup.get('credentialStore')!.setValue(selectedCredentialStore);
        });

        this.formGroup = this._injector.get<FormBuilder>(FormBuilder).group({
            'name': [
                this.context.data.name,
                Validators.compose([
                    UiValidators.required(),
                    Validators.maxLength(UI_ASSET_NAME_MAX_LENGTH),
                ])],
            'description': [
                this.context.data.description,
                Validators.maxLength(UI_ASSET_DESCRIPTION_MAX_LENGTH),
            ],
            'type': [[this.context.data.type]],
            'credentialStore': [],
        });

        this.formGroup.get('credentialStore')!.valueChanges
            .pipe(
                takeUntil(this._destroyed$),
            ).subscribe(([value]: ISuggestValueData<ICredentialStore>[]) => {
                const storeDetails = value && value.data && value.data.details;
                this.isReadOnly$.next(storeDetails ? storeDetails.isReadOnly : false);
                this.credentialStoreChange.next();
            });
    }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
        this.credentialStoreChange.complete();
    }

    public onTypeChange(event: ISuggestValue) {
        this.context.data.type = event.id as IAssetType;
    }

    public onSubmit(ev: Event) {
        ev.preventDefault();

        this.loading$.next(true);

        if (this.context.isNewEntry) {
            this._assetService
                .add(this._generatePayload())
                .pipe(
                    finalize(
                        () => this.loading$.next(false),
                    ),
                )
                .subscribe(() => {
                    this._dialogRef.close({
                        type: ModalResultType.Add,
                    });
                });
            this.loading$.next(false);
        } else {
            this._assetService
                .update(this.context.data.id, this._generatePayload())
                .pipe(
                    finalize(
                        () => this.loading$.next(false),
                    ),
                )
                .subscribe(() => {
                    this._dialogRef.close({
                        type: ModalResultType.Update,
                    });
                });
        }
    }

    private _generatePayload(): IAsset {
        const model = this.singleValueEditor.formGroup.get('value')!.value;
        const payload = generatePayloadFrom(
            this.context.data,
            this.context.data.type,
            this.isDefaultChecked ? model : defaultValueFor(this.context.data.type, null, this.isReadOnly$.value),
            this.isReadOnly$.value,
        ) as IAsset;
        payload.name = this.formGroup.get('name')!.value;
        payload.description = this.formGroup.get('description')!.value;
        payload.scope = this.isDefaultChecked && !this.hasRobots ?
            AssetScope[AssetScope.Global] : AssetScope[AssetScope.PerRobot];

        const [credentialStore] = this.formGroup.get('credentialStore')!.value as ISuggestValueData<ICredentialStore>[];
        payload.credentialStoreId = payload.type === AssetType.Credential && this.isDefaultChecked
            ? credentialStore.data!.id
            : undefined;
        payload.canBeDeleted = this.context.data.canBeDeleted;
        payload.hasDefaultValue = this.isDefaultChecked;
        payload.robotValues = payload.scope === AssetScope[AssetScope.PerRobot] ?
            this.robotValueEditor.robotAssets.values :
            [];

        return payload;
    }
}
